import { Table, TableBody } from "@/components/ui/table";
import { useState } from "react";
import { LeadSourceDialog } from "./LeadSourceDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { LeadSourceRow } from "./components/LeadSourceRow";
import { LeadSourceTableHeader } from "./components/LeadSourceTableHeader";
import { useLeadSources } from "@/hooks/useLeadSources";
import { useLeadSourceMutations } from "./hooks/useLeadSourceMutations";

export const LeadSourcesTable = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingLeadSource, setEditingLeadSource] = useState<{id: string, name: string, description: string} | null>(null);
  const [deletingLeadSourceId, setDeletingLeadSourceId] = useState<string | null>(null);
  
  const { data: leadSources } = useLeadSources();
  const { createLeadSource, updateLeadSource, deleteLeadSource, updateOrder } = useLeadSourceMutations();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleCreate = async (name: string, description: string) => {
    await createLeadSource.mutateAsync({
      name,
      description,
      order: leadSources ? leadSources.length + 1 : 1
    });
    setShowDialog(false);
  };

  const handleUpdate = async (name: string, description: string) => {
    if (editingLeadSource) {
      await updateLeadSource.mutateAsync({
        id: editingLeadSource.id,
        name,
        description
      });
      setEditingLeadSource(null);
    }
  };

  const handleDelete = async () => {
    if (deletingLeadSourceId) {
      await deleteLeadSource.mutateAsync(deletingLeadSourceId);
      setDeletingLeadSourceId(null);
      setShowDeleteDialog(false);
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    
    if (active.id !== over.id && leadSources) {
      const oldIndex = leadSources.findIndex((source) => source.id === active.id);
      const newIndex = leadSources.findIndex((source) => source.id === over.id);
      
      const newLeadSources = [...leadSources];
      const [removed] = newLeadSources.splice(oldIndex, 1);
      newLeadSources.splice(newIndex, 0, removed);
      
      await updateOrder.mutateAsync(newLeadSources);
    }
  };

  return (
    <div className="space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Table>
          <LeadSourceTableHeader />
          <TableBody>
            <SortableContext
              items={leadSources?.map(source => source.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {leadSources?.map((source) => (
                <LeadSourceRow
                  key={source.id}
                  id={source.id}
                  name={source.name}
                  description={source.description || ''}
                  onEdit={() => setEditingLeadSource(source)}
                  onDelete={() => {
                    setDeletingLeadSourceId(source.id);
                    setShowDeleteDialog(true);
                  }}
                />
              ))}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>

      <LeadSourceDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        onSubmit={handleCreate}
        mode="create"
      />

      <LeadSourceDialog
        isOpen={!!editingLeadSource}
        onClose={() => setEditingLeadSource(null)}
        onSubmit={handleUpdate}
        initialName={editingLeadSource?.name}
        initialDescription={editingLeadSource?.description}
        mode="edit"
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the lead source.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};